import { atom } from 'jotai';
import { unwrap } from 'jotai/utils';
import { atomWithSuspenseQuery } from 'jotai-tanstack-query';
import { fetchClientAtom } from '@/atoms/queries/client';
import { convertFilterParamsToString } from '@/utils/api';
import { eventDataFilterParamsAtom } from './eventData';

// Strip period/minute filters before passing params to the endpoint.
// Available periods are derived from the player/competition/season scope only —
// passing the active period filter would create a chicken-and-egg problem where
// filtering by period 1 hides periods 2-4 from the available options.
const scopeOnlyEventFilterParamsAtom = atom(get => {
  const params = get(eventDataFilterParamsAtom);
  const { 'event.period': _period, ...inWithoutPeriod } = params.in ?? {};
  const { 'event.attributes.minute': _gteMinute, ...gteWithoutMinute } = params.gte ?? {};
  const { 'event.attributes.minute': _lteMinute, ...lteWithoutMinute } = params.lte ?? {};
  return {
    ...params,
    in: inWithoutPeriod,
    gte: gteWithoutMinute,
    lte: lteWithoutMinute,
    groups: undefined,
  };
});

const rawAvailableEventPeriodsAtom = atomWithSuspenseQuery(get => {
  const { fetch } = get(fetchClientAtom);
  const scopeParams = get(scopeOnlyEventFilterParamsAtom);
  const queryKey = ['available-event-periods', scopeParams] as const;
  const queryFn = async ({ queryKey: [, filterParams] }: { queryKey: typeof queryKey }) =>
    (await fetch(`/available-event-periods${convertFilterParamsToString(filterParams)}`)) as Promise<number[]>;
  return { queryKey, queryFn };
});

export const availableEventPeriodsAtom = atom(async get => (await get(rawAvailableEventPeriodsAtom)).data);

export const unwrappedAvailableEventPeriodsAtom = unwrap(availableEventPeriodsAtom, prev => prev ?? []);
